import { PerformanceDatapoint } from 'owa-analytics';
import { freeBusyCache, FreeBusyType } from 'owa-freebusy-cache';
import { CalendarEvent } from 'owa-calendar-types';
import getCalendarOwnerFromItem from 'owa-calendar-forms-common/lib/utils/getCalendarOwnerFromItem';
import AttendeeType from 'owa-service/lib/contract/AttendeeType';
import isRecipientDistributionList from 'owa-calendar-compose-form-store/lib/utils/isRecipientDistributionList';

export default function logFreeBusyInfo(datapoint: PerformanceDatapoint, event: CalendarEvent) {
    const attendeeCollection = [].concat(
        event.RequiredAttendees.slice(),
        event.OptionalAttendees.slice()
    );

    datapoint.addCustomData(returnCustomDataForCollection(attendeeCollection, 'Attendees', event));

    datapoint.addCustomData(returnCustomDataForCollection(attendeeCollection, 'DLs', event));

    if (event.Resources && event.Resources.length > 0) {
        datapoint.addCustomData(returnCustomDataForCollection(event.Resources, 'Resources', event));
    }

    const organizerMailbox = getCalendarOwnerFromItem(event);
    if (organizerMailbox) {
        const organizer = [{ Mailbox: organizerMailbox } as AttendeeType];
        datapoint.addCustomData(returnCustomDataForCollection(organizer, 'Organizer', event));
    }
}

/**
 * Returns the propertybag that can be added to the custom data which represents the freeBusyInfo
 * @param recipients recipients for which we need the customData
 * @param prefix Prefix, could be Attendees/DLs/Resources/Organizer
 */
function returnCustomDataForCollection(
    recipients: AttendeeType[],
    prefix: 'Attendees' | 'DLs' | 'Resources' | 'Organizer',
    { Start, End, IsAllDayEvent }: CalendarEvent
) {
    const freeBusyInfo = recipients
        .filter(recipient => recipient.Mailbox != null)
        .filter(recipient => {
            return prefix === 'DLs'
                ? isRecipientDistributionList(recipient)
                : !isRecipientDistributionList(recipient);
        })
        .map(recipient => {
            return freeBusyCache.getAggregatedFreeBusy(
                recipient.Mailbox.EmailAddress,
                Start,
                End,
                IsAllDayEvent
            );
        });

    // Consolidate the freebusy info into a map
    const freeBusyInfoMap: Map<FreeBusyType, number> = new Map<FreeBusyType, number>();
    freeBusyInfo.forEach(freeBusy => {
        const statusCount = freeBusyInfoMap.get(freeBusy);
        if (!statusCount) {
            freeBusyInfoMap.set(freeBusy, 1);
        } else {
            freeBusyInfoMap.set(freeBusy, statusCount + 1);
        }
    });

    // Convert the map into a collection
    let propertyBag = {};

    freeBusyInfoMap.forEach((count: number, freeBusyType: FreeBusyType) => {
        const freeBusyString = getFreeBusyString(freeBusyType, prefix);
        propertyBag[freeBusyString] = count;
    });

    return propertyBag;
}

/**
 * Returns the freebusy string that needs to be logged for the specified freebusytype and prefix
 * For instance, it returns AttendeesFree, AttendeesBusy, etc
 */
function getFreeBusyString(freeBusy: FreeBusyType, prefix: string): string {
    let str: string;
    switch (freeBusy) {
        case FreeBusyType.Free:
            str = 'Free';
            break;
        case FreeBusyType.Tentative:
            str = 'Tentative';
            break;
        case FreeBusyType.Busy:
            str = 'Busy';
            break;
        case FreeBusyType.OOF:
            str = 'OOF';
            break;
        case FreeBusyType.WorkingElsewhere:
            str = 'WorkingElsewhere';
            break;
        case FreeBusyType.NoData:
            str = 'NoData';
            break;
        case FreeBusyType.Loading:
            str = 'Loading';
            break;
    }

    return prefix + str;
}
